import { atom } from 'jotai';

/**
 * Stores the active session ID when user enters a session page.
 * This persists across navigation so the LiveTranscriptionIndicator
 * can show session status when user navigates away from the session.
 *
 * # Asymmetric-clear contract — read before touching writers
 *
 * The atom is **cleared on both pause AND stop**. This is intentional for
 * `useBlockNextNavigationGlobal`, which uses a non-null value to block
 * cross-session navigation only while truly RECORDING (paused sessions
 * are navigable). The cost is that downstream consumers who need to track
 * "is there a session in flight" through the paused state cannot trust
 * the live atom after it clears. They retain the last non-null id and treat it
 * as paused only while the `recording_status` cache reports PAUSED:
 *
 *   - `use-mini-mode-state.ts`         → `retainedSessionIdRef`
 *   - `useAutoMediaSession.ts`         → `lastActiveSessionIdRef`
 *   - session list / pending recording → `inFlightRecordingSessionIdAtom`
 *
 * New shared consumers that need the paused session should read
 * `inFlightRecordingSessionIdAtom` only while `recording_status` is PAUSED; use
 * a retained ref for component-local query lifetime or teardown state. Do not
 * change the pause-clear semantics without also updating
 * `useBlockNextNavigationGlobal`.
 *
 * # Why this isn't replaced by server state
 *
 * `recording_status` from the BE query cache cannot replace this atom:
 * `useMutateUpdateSessionRecordingStatus` short-circuits the BE PATCH for
 * `RECORDING`, so RECORDING is an optimistic local-only write. A
 * background refetch can overwrite it back to IDLE silently. The atom
 * remains the canonical local synchronous "recording is RIGHT NOW
 * happening in this tab" signal.
 */
export const activeSessionIdAtom = atom<string | null>(null);

/**
 * Last session that armed `activeSessionIdAtom`. Survives web/mic pause, which
 * clears the live atom so paused sessions stay navigable. List Resume/End and
 * pending resume/stop read this only while `recording_status` is PAUSED.
 */
export const inFlightRecordingSessionIdAtom = atom<string | null>(null);

export type PendingRecordingAction = {
  action: 'stop' | 'resume' | 'pause';
  sessionId: string;
  requestedAt: number;
  source: 'live-transcription-indicator' | 'start-now-list';
};

/**
 * Time-bounded recording intent consumed after its exact session page opens.
 */
export const pendingRecordingActionAtom = atom<PendingRecordingAction | null>(null);
